import { Box3 } from 'three';
import type { BufferGeometry, Object3D } from 'three';
import { SceneObjectMembershipTracker } from '../viewer/scene';

/**
 * 正交裁剪专用世界范围缓存，不与视口关注范围共用缓冲。
 * 正式实例盒来自已有空间索引；SDK 原生对象可直接赋值，因此只读校验去重后的属性快照，
 * 不篡改 Object3D 属性、不沿父链重复判定，也不在相机变化时重算未变化模型的包围盒。
 */
export class CameraClippingBoundsCache {
    /** 总范围由本缓存拥有，输出只复制，不暴露内部可变对象。 */
    private readonly bounds = new Box3();
    /** 每个依赖只保留一次，避免同一祖先随多个实例重复检查。 */
    private readonly dependencies = new Map<Object3D, CameraClippingDependencyState>();
    /** 只保存正式实例组/普通模型的世界合并盒，不复制已有逐槽 AABB 树。 */
    private readonly ownerBounds = new Map<Object3D, Box3>();
    /** 依赖变化到拥有者的反向索引，父装配变化只影响其后代。 */
    private readonly ownersByDependency = new Map<Object3D, Set<Object3D>>();
    /** 本轮真正需要刷新范围的拥有者。 */
    private readonly dirtyOwners = new Set<Object3D>();
    /** 本轮需要原位更新属性快照的依赖。 */
    private readonly changedDependencies = new Set<CameraClippingDependencyState>();
    /** 当前构建反向依赖或局部读取的拥有者，避免临时闭包。 */
    private activeOwner: Object3D | null = null;
    /** 原生几何直接写入 position 并更新版本时，下一次聚合前刷新其标准局部盒。 */
    private dirtyGeometries = new WeakSet<BufferGeometry>();
    /** 读取属性时复用扁平缓冲，不在热路径创建向量或数组。 */
    private readonly transform = new Float64Array(32);
    /** 当前物理场景。 */
    private scene: Object3D | null = null;
    /** 优先复用 Viewer 的成员源。 */
    private membership: SceneObjectMembershipSource | null = null;
    /** 独立 SDK 或 Viewer 尚未初始化 MeshManager 时的自有成员源。 */
    private ownedMembership: SceneObjectMembershipTracker | null = null;
    /** 成员退订句柄。 */
    private unsubscribe: (() => void) | null = null;
    /** 模型成员变化只置脏，不在事件回调同步遍历场景。 */
    private dirty = true;
    /** 非静态模型保守回退，不能以静态缓存漏掉动画。 */
    private dynamic = false;
    /** 上次聚合采用的正式实例空间版本。 */
    private revision = -1;
    /** 单独观察 Unit 成员结构变化，不能让注销后的源映射继续被复用。 */
    private membershipRevision = -1;
    /** 生命周期结束后不再保留模型引用。 */
    private disposed = false;
    /** 累计命中次数。 */
    private cacheHits = 0;
    /** 累计聚合次数。 */
    private rebuilds = 0;
    /** 累计增量更新的拥有者数量。 */
    private refreshedOwners = 0;
    /** 稳定范围访问回调，局部刷新时不能把子级其它拥有者再次并入当前范围。 */
    private readonly collectBounds: CameraModelBoundsVisitor = (source, bounds) => {
        const owner = this.source.resolveClippingOwner(source);
        if (this.activeOwner && owner !== this.activeOwner) return;
        let cached = this.ownerBounds.get(owner);
        if (!cached) { cached = new Box3(); this.ownerBounds.set(owner, cached); }
        cached.union(bounds);
    };
    /** 已发布的实例空间变化只登记对应 Unit，不触发全场景失效。 */
    private readonly markOwnerDirty = (owner: Object3D): void => { this.dirtyOwners.add(owner); };
    /** 延迟刷新仍被当前查询依赖的几何；辅助对象替换掉的旧几何不因脏标记被保活。 */
    private readonly refreshGeometryDependency = (object: Object3D): void => {
        const geometry = (object as CameraClippingDependencyObject).geometry;
        if (geometry && this.dirtyGeometries.has(geometry)) {
            geometry.computeBoundingBox();
            this.dirtyGeometries.delete(geometry);
        }
    };

    /**
     * @param source 只读空间源，不拥有它的生命周期。
     * @param predicate 既有相机资格规则，必须与视图定向/旋转中心规则一致。
     * @param getMembership 延迟提供 Viewer 成员源，避免相机初始化早于 MeshManager 时重复持有。
     */
    public constructor(
        private readonly source: CameraClippingBoundsSourcePort,
        private readonly predicate: (object: Object3D) => boolean,
        private readonly getMembership?: () => SceneObjectMembershipSource | undefined,
    ) {}

    /**
     * 复制当前有效世界范围。纯相机变化只校验直接属性和实例版本，不重算范围。
     * @param scene 当前模型场景。
     * @param target 调用方复用盒。
     * @returns 是否存在有限、非空范围。
     */
    public copyBounds(scene: Object3D, target: Box3): boolean {
        target.makeEmpty();
        if (this.disposed) return false;
        this.attachMembership(scene);
        const revision = this.source.getClippingRevision();
        this.dirty ||= this.dynamic || this.source.getClippingMembershipRevision() !== this.membershipRevision;
        this.dirtyOwners.clear();
        this.changedDependencies.clear();
        if (revision !== this.revision) this.source.forEachChangedClippingOwner(this.revision, this.markOwnerDirty);
        // 即使成员已置脏也校验几何版本，避免恰逢增删时沿用已过期 geometry.boundingBox。
        for (const dependency of this.dependencies.values()) {
            if (!this.hasChanged(dependency)) continue;
            const owners = this.ownersByDependency.get(dependency.object);
            // 导航器等资格规则排除的几何也会跟随相机更新；它们的纯位姿/几何变化不属于模型变化。
            // 已排除几何的可见性变化仍可忽略；容器显隐、父级、名称或辅助语义改变需要重新发现，
            // 避免漏掉原本隐藏的业务子树。真正再次符合资格的几何不会进入此分支。
            if (!owners && !this.predicate(dependency.object)
                && (dependency.visible === dependency.object.visible || Boolean(dependency.geometry))
                && dependency.parent === dependency.object.parent
                && dependency.name === dependency.object.name && dependency.flags === this.readFlags(dependency.object)) {
                this.updateDependency(dependency);
                continue;
            }
            this.changedDependencies.add(dependency);
            if (!owners || dependency.excluded !== this.source.isDerived(dependency.object)) this.dirty = true;
            else for (const owner of owners) this.dirtyOwners.add(owner);
        }
        if (this.dirty) this.rebuild(scene);
        else if (this.dirtyOwners.size) this.refreshOwners();
        else this.cacheHits++;
        target.copy(this.bounds);
        return !target.isEmpty();
    }

    /** @returns 显式统计；不在普通相机帧中创建统计对象。 */
    public getStats(): CameraClippingBoundsStats {
        return { cacheHits: this.cacheHits, rebuilds: this.rebuilds, dependencies: this.dependencies.size, refreshedOwners: this.refreshedOwners };
    }

    /** 幂等解除观察和模型引用；不会释放借用的模型、几何或实例索引。 */
    public dispose(): void {
        this.disposed = true;
        this.releaseMembership();
        this.dependencies.clear();
        this.ownerBounds.clear();
        this.ownersByDependency.clear();
        this.dirtyOwners.clear();
        this.changedDependencies.clear();
        this.dirtyGeometries = new WeakSet();
        this.bounds.makeEmpty();
        this.scene = null;
        this.activeOwner = null;
    }

    /** @param scene 查询根；场景替换或共享成员源首次可用时重新订阅。 */
    private attachMembership(scene: Object3D): void {
        const shared = this.getMembership?.();
        if (this.scene === scene && this.membership && (!shared || shared === this.membership)) return;
        this.releaseMembership();
        this.scene = scene;
        this.dependencies.clear();
        this.dirty = true;
        this.ownedMembership = shared ? null : new SceneObjectMembershipTracker(scene);
        this.membership = shared ?? this.ownedMembership;
        this.unsubscribe = this.membership!.subscribe({
            onObjectAdded: this.handleMembershipChange,
            onObjectRemoved: this.handleMembershipChange,
        });
    }

    /** 释放自有回退源；共享源仅退订，禁止由相机销毁。 */
    private releaseMembership(): void {
        this.unsubscribe?.();
        this.unsubscribe = null;
        this.ownedMembership?.dispose();
        this.ownedMembership = null;
        this.membership = null;
    }

    /** @param object 变化成员；派生覆盖层事件不能使真实模型范围反复重算。 */
    private readonly handleMembershipChange = (object: Object3D): void => {
        let current: Object3D | null = object;
        while (current) {
            if (this.source.isDerived(current)) return;
            current = current.parent;
        }
        this.dirty = true;
    };

    /** @param scene 仅模型依赖变化时重新聚合，正常相机帧不进入此方法。 */
    private rebuild(scene: Object3D): void {
        this.dirty = true;
        this.source.forEachClippingDependency(scene, this.refreshGeometryDependency);
        this.ownerBounds.clear();
        this.activeOwner = null;
        this.source.forEachClippingModel(scene, this.predicate, this.collectBounds);
        this.dependencies.clear();
        this.ownersByDependency.clear();
        this.dynamic = false;
        this.source.forEachClippingDependency(scene, this.captureDependency);
        // 反向关系只随结构重建；一次父级变换可直接定位受影响的正式模型/Unit。
        for (const owner of this.ownerBounds.keys()) {
            this.activeOwner = owner;
            this.source.forEachClippingDependency(owner, this.bindDependencyOwner);
        }
        this.activeOwner = null;
        this.aggregateBounds();
        // 空间读取可能确认几何修订，记录最终版本，避免下一次无变化查询再次聚合。
        this.revision = this.source.getClippingRevision();
        this.membershipRevision = this.source.getClippingMembershipRevision();
        this.rebuilds++;
        this.dirty = false;
    }

    /** 只刷新变化组的范围；既有槽位 AABB 仍由原索引增量维护。 */
    private refreshOwners(): void {
        this.dirty = true;
        try {
            for (const owner of this.dirtyOwners) {
                this.source.forEachClippingDependency(owner, this.refreshGeometryDependency);
                this.ownerBounds.get(owner)?.makeEmpty();
                this.activeOwner = owner;
                this.source.forEachClippingModel(owner, this.predicate, this.collectBounds);
                this.refreshedOwners++;
            }
        } finally { this.activeOwner = null; }
        for (const dependency of this.changedDependencies) this.updateDependency(dependency);
        this.aggregateBounds();
        this.revision = this.source.getClippingRevision();
        this.dirty = false;
    }

    /** 分组盒允许变空或缩小，每次从空盒合并，避免只增不减的错误。 */
    private aggregateBounds(): void {
        this.bounds.makeEmpty();
        for (const bounds of this.ownerBounds.values()) this.bounds.union(bounds);
    }

    /** @param object 当前拥有者依赖；生命周期由缓存及共享源管理。 */
    private readonly bindDependencyOwner = (object: Object3D): void => {
        if (!this.activeOwner) return;
        let owners = this.ownersByDependency.get(object);
        if (!owners) { owners = new Set(); this.ownersByDependency.set(object, owners); }
        owners.add(this.activeOwner);
    };

    /** @param state 原位刷新已变化快照，拖动时不反复创建依赖对象和矩阵数组。 */
    private updateDependency(state: CameraClippingDependencyState): void {
        const object = state.object;
        const geometry = object.geometry;
        const position = geometry?.getAttribute('position');
        this.writeTransform(object);
        state.transform.set(this.transform);
        state.parent = object.parent; state.name = object.name; state.visible = object.visible;
        state.matrixAutoUpdate = object.matrixAutoUpdate; state.matrixWorldAutoUpdate = object.matrixWorldAutoUpdate;
        state.flags = this.readFlags(object); state.excluded = this.source.isDerived(object);
        state.geometry = geometry; state.position = position;
        state.positionVersion = position ? ('data' in position ? position.data.version : position.version) : -1;
        state.positionCount = position?.count ?? 0;
        state.index = geometry?.index; state.indexVersion = geometry?.index?.version ?? -1;
        state.instanceMatrix = object.instanceMatrix; state.instanceVersion = object.instanceMatrix?.version ?? -1;
        state.instanceCount = object.count ?? -1;
    }

    /** @param object 保存直接属性，父级本身也是独立依赖，不重复存储整个祖先链。 */
    private readonly captureDependency = (object: Object3D): void => {
        if (this.dependencies.has(object)) return;
        const candidate = object as CameraClippingDependencyObject;
        const geometry = candidate.geometry;
        const position = geometry?.getAttribute('position');
        this.writeTransform(object);
        this.dependencies.set(object, {
            object: candidate, parent: object.parent, name: object.name, visible: object.visible,
            matrixAutoUpdate: object.matrixAutoUpdate, matrixWorldAutoUpdate: object.matrixWorldAutoUpdate,
            transform: this.transform.slice(), flags: this.readFlags(candidate), geometry, position,
            excluded: this.source.isDerived(object),
            positionVersion: position ? ('data' in position ? position.data.version : position.version) : -1,
            positionCount: position?.count ?? 0, index: geometry?.index, indexVersion: geometry?.index?.version ?? -1,
            instanceMatrix: candidate.instanceMatrix, instanceVersion: candidate.instanceMatrix?.version ?? -1,
            instanceCount: candidate.count ?? -1,
        });
        this.dynamic ||= candidate.isSkinnedMesh === true || Boolean(geometry?.morphAttributes.position?.length);
    };

    /** @param state 旧快照。 @returns 是否存在影响模型范围的变化。 */
    private hasChanged(state: CameraClippingDependencyState): boolean {
        const object = state.object;
        if (state.excluded) return this.readFlags(object) !== state.flags || object.name !== state.name;
        const geometry = object.geometry;
        const position = geometry?.getAttribute('position');
        const version = position ? ('data' in position ? position.data.version : position.version) : -1;
        const geometryChanged = geometry !== state.geometry || position !== state.position
            || version !== state.positionVersion || (position?.count ?? 0) !== state.positionCount
            || geometry?.index !== state.index || (geometry?.index?.version ?? -1) !== state.indexVersion;
        if (geometryChanged && geometry) this.dirtyGeometries.add(geometry);
        if (geometryChanged || object.parent !== state.parent || object.name !== state.name
            || object.visible !== state.visible || object.matrixAutoUpdate !== state.matrixAutoUpdate
            || object.matrixWorldAutoUpdate !== state.matrixWorldAutoUpdate || this.readFlags(object) !== state.flags
            || object.instanceMatrix !== state.instanceMatrix || (object.instanceMatrix?.version ?? -1) !== state.instanceVersion
            || (object.count ?? -1) !== state.instanceCount) return true;
        // 热态直接比较必要分量，禁止先填充临时数组再扫描 32 个元素。
        const values = state.transform;
        if (object.matrixAutoUpdate) {
            const p = object.position, q = object.quaternion, s = object.scale;
            if (p.x !== values[0] || p.y !== values[1] || p.z !== values[2]
                || q.x !== values[3] || q.y !== values[4] || q.z !== values[5] || q.w !== values[6]
                || s.x !== values[7] || s.y !== values[8] || s.z !== values[9]) return true;
        } else {
            for (let index = 0; index < 16; index++) if (object.matrix.elements[index] !== values[index]) return true;
        }
        if (!object.matrixWorldAutoUpdate) {
            for (let index = 0; index < 16; index++) if (object.matrixWorld.elements[index] !== values[16 + index]) return true;
        }
        return false;
    }

    /** @param object 读取已有业务/辅助标记；不进行动态反射或祖先查找。 */
    private readFlags(object: CameraClippingDependencyObject): number {
        return (object.ignore ? 1 : 0) | (object.userData.isImportedModelOverlay ? 2 : 0)
            | (object.userData.skipSelectionSubtree ? 4 : 0) | (object.isFeatureFace ? 8 : 0)
            | (object.isFeatureLine ? 16 : 0) | (object.isFeaturePoint ? 32 : 0)
            | (object.isInfiniteLength ? 64 : 0);
    }

    /**
     * 直接读取变换分量。自动矩阵在渲染中派生更新，不应让其更新时间触发伪失效。
     * @param object 普通对象或逻辑依赖。
     */
    private writeTransform(object: Object3D): void {
        const values = this.transform;
        values.fill(0);
        if (object.matrixAutoUpdate) {
            const p = object.position, q = object.quaternion, s = object.scale;
            values[0] = p.x; values[1] = p.y; values[2] = p.z;
            values[3] = q.x; values[4] = q.y; values[5] = q.z; values[6] = q.w;
            values[7] = s.x; values[8] = s.y; values[9] = s.z;
        } else {
            for (let index = 0; index < 16; index++) values[index] = object.matrix.elements[index];
        }
        if (!object.matrixWorldAutoUpdate) {
            for (let index = 0; index < 16; index++) values[16 + index] = object.matrixWorld.elements[index];
        }
    }
}
